/**
 * GossipSub Pub/Sub Setup
 *
 * Handles room-based messaging through GossipSub topics.
 */

import type { Libp2p } from 'libp2p'

// Topic patterns
export const TOPICS = {
  // Global presence topic - relay subscribes to this and forwards to all clients
  // This is critical for peer discovery across all rooms
  globalPresence: '/crowdtalkie/global/presence',
  // Room discovery by geographic area
  discovery: (geohash6: string) => `/crowdtalkie/discovery/${geohash6}`,
  // Room-specific topics
  room: {
    messages: (roomId: string) => `/crowdtalkie/room/${roomId}/messages`,
    state: (roomId: string) => `/crowdtalkie/room/${roomId}/state`,
    sync: (roomId: string) => `/crowdtalkie/room/${roomId}/sync`, // Full state sync requests/responses
    speaking: (roomId: string) => `/crowdtalkie/room/${roomId}/speaking`,
    audio: (roomId: string) => `/crowdtalkie/room/${roomId}/audio`,
  },
  // ICS Hierarchy topics
  hierarchy: {
    /** Reports escalated from sectors to command */
    escalations: (commandRoomId: string) => `/crowdtalkie/hierarchy/${commandRoomId}/escalations`,
    /** Broadcasts from command to sectors */
    broadcasts: (commandRoomId: string) => `/crowdtalkie/hierarchy/${commandRoomId}/broadcasts`,
    /** Resource movement assignments */
    assignments: (commandRoomId: string) => `/crowdtalkie/hierarchy/${commandRoomId}/assignments`,
    /** Hierarchy structure changes */
    structure: (commandRoomId: string) => `/crowdtalkie/hierarchy/${commandRoomId}/structure`,
    /** Sector status updates (heartbeats) */
    sectorStatus: (commandRoomId: string) => `/crowdtalkie/hierarchy/${commandRoomId}/status`,
    /** Cross-room organizer assignments */
    organizerAssignments: (commandRoomId: string) => `/crowdtalkie/hierarchy/${commandRoomId}/organizer-assignments`,
    /** Remote commands from command to sectors */
    commands: (commandRoomId: string) => `/crowdtalkie/hierarchy/${commandRoomId}/commands`,
    /** Boundary updates for sectors */
    boundaries: (commandRoomId: string) => `/crowdtalkie/hierarchy/${commandRoomId}/boundaries`,
  },
}

export type TopicHandler = (data: Uint8Array, from: string) => void

interface TopicSubscription {
  topic: string
  handler: TopicHandler
}

const subscriptions: Map<string, TopicSubscription[]> = new Map()

/**
 * Get the GossipSub pubsub service from the node
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getPubsub(node: Libp2p): any {
  const pubsub = (node.services as { pubsub?: unknown }).pubsub
  if (!pubsub) {
    throw new Error('PubSub service not available on node')
  }
  return pubsub
}

/**
 * Subscribe to a topic
 */
export function subscribeTopic(
  node: Libp2p,
  topic: string,
  handler: TopicHandler
): () => void {
  const pubsub = getPubsub(node)

  // Add handler to subscriptions
  const existing = subscriptions.get(topic) ?? []
  existing.push({ topic, handler })
  subscriptions.set(topic, existing)

  // Subscribe to topic if first subscription
  if (existing.length === 1) {
    pubsub.subscribe(topic)
    console.log('[PubSub] Subscribed to topic:', topic)

    // Log mesh peers for this topic after a delay (mesh takes time to form)
    setTimeout(() => {
      try {
        const subscribers = pubsub.getSubscribers?.(topic) ?? []
        const meshPeers = pubsub.getMeshPeers?.(topic) ?? []
        console.log('[PubSub] Topic', topic.substring(0, 40) + '...', {
          subscribers: subscribers.length,
          meshPeers: meshPeers.length,
        })
      } catch {
        // Ignore - some methods may not be available
      }
    }, 3000)
  }

  // Return unsubscribe function
  return () => {
    const handlers = subscriptions.get(topic)
    if (handlers) {
      const index = handlers.findIndex((h) => h.handler === handler)
      if (index !== -1) {
        handlers.splice(index, 1)
        if (handlers.length === 0) {
          pubsub.unsubscribe(topic)
          subscriptions.delete(topic)
          console.log('[PubSub] Unsubscribed from topic:', topic)
        }
      }
    }
  }
}

/**
 * Publish a message to a topic
 */
export async function publishToTopic(
  node: Libp2p,
  topic: string,
  data: Uint8Array
): Promise<void> {
  const pubsub = getPubsub(node)

  // Log mesh state before publishing
  try {
    const meshPeers = pubsub.getMeshPeers?.(topic) ?? []
    const subscribers = pubsub.getSubscribers?.(topic) ?? []
    console.log('[PubSub] Publishing to topic:', topic.substring(0, 50) + '...', {
      size: data.length,
      meshPeers: meshPeers.length,
      subscribers: subscribers.length,
    })
  } catch {
    console.log('[PubSub] Publishing to topic:', topic.substring(0, 50) + '...', 'size:', data.length)
  }

  await pubsub.publish(topic, data)
}

/**
 * Setup message handler for the node
 * Call this once after node creation to route messages to handlers
 */
export function setupMessageHandler(node: Libp2p): void {
  const pubsub = getPubsub(node)

  pubsub.addEventListener('message', (evt: { detail: { topic: string; data: Uint8Array; from: { toString: () => string } } }) => {
    const { topic, data, from } = evt.detail
    const handlers = subscriptions.get(topic)

    console.log('[PubSub] Received message on topic:', topic.substring(0, 50) + '...', 'from:', from.toString().substring(0, 12) + '...', 'size:', data.length)

    if (handlers) {
      console.log('[PubSub] Found', handlers.length, 'handlers for topic')
      for (const { handler } of handlers) {
        try {
          handler(data, from.toString())
        } catch (error) {
          console.error('[PubSub] Error in message handler:', error)
        }
      }
    } else {
      console.warn('[PubSub] No handlers registered for topic:', topic)
    }
  })

  // Also listen for subscription changes
  pubsub.addEventListener('subscription-change', (evt: { detail: { peerId: { toString: () => string }; subscriptions: { topic: string; subscribe: boolean }[] } }) => {
    const { peerId, subscriptions: subs } = evt.detail
    for (const sub of subs) {
      console.log('[PubSub] Peer', peerId.toString().substring(0, 12) + '...', sub.subscribe ? 'subscribed to' : 'unsubscribed from', sub.topic.substring(0, 50) + '...')
    }
  })
}

/**
 * Subscribe to room discovery for a geographic area
 */
export function subscribeToDiscovery(
  node: Libp2p,
  geohash6: string,
  handler: TopicHandler
): () => void {
  const topic = TOPICS.discovery(geohash6)
  return subscribeTopic(node, topic, handler)
}

/**
 * Subscribe to a room's message topic
 */
export function subscribeToRoomMessages(
  node: Libp2p,
  roomId: string,
  handler: TopicHandler
): () => void {
  const topic = TOPICS.room.messages(roomId)
  return subscribeTopic(node, topic, handler)
}

/**
 * Subscribe to a room's state topic (CRDT sync)
 */
export function subscribeToRoomState(
  node: Libp2p,
  roomId: string,
  handler: TopicHandler
): () => void {
  const topic = TOPICS.room.state(roomId)
  return subscribeTopic(node, topic, handler)
}

/**
 * Subscribe to a room's speaking queue topic
 */
export function subscribeToSpeakingQueue(
  node: Libp2p,
  roomId: string,
  handler: TopicHandler
): () => void {
  const topic = TOPICS.room.speaking(roomId)
  return subscribeTopic(node, topic, handler)
}

/**
 * Subscribe to a room's audio topic
 */
export function subscribeToRoomAudio(
  node: Libp2p,
  roomId: string,
  handler: TopicHandler
): () => void {
  const topic = TOPICS.room.audio(roomId)
  return subscribeTopic(node, topic, handler)
}

/**
 * Publish a message to a room
 */
export async function publishRoomMessage(
  node: Libp2p,
  roomId: string,
  data: Uint8Array
): Promise<void> {
  const topic = TOPICS.room.messages(roomId)
  await publishToTopic(node, topic, data)
}

/**
 * Publish room state update
 */
export async function publishRoomState(
  node: Libp2p,
  roomId: string,
  data: Uint8Array
): Promise<void> {
  const topic = TOPICS.room.state(roomId)
  await publishToTopic(node, topic, data)
}

/**
 * Publish speaking queue update
 */
export async function publishSpeakingUpdate(
  node: Libp2p,
  roomId: string,
  data: Uint8Array
): Promise<void> {
  const topic = TOPICS.room.speaking(roomId)
  await publishToTopic(node, topic, data)
}

/**
 * Publish audio data
 */
export async function publishAudio(
  node: Libp2p,
  roomId: string,
  data: Uint8Array
): Promise<void> {
  const topic = TOPICS.room.audio(roomId)
  await publishToTopic(node, topic, data)
}

/**
 * Subscribe to room sync topic (for full state sync requests/responses)
 */
export function subscribeToRoomSync(
  node: Libp2p,
  roomId: string,
  handler: TopicHandler
): () => void {
  const topic = TOPICS.room.sync(roomId)
  return subscribeTopic(node, topic, handler)
}

/**
 * Publish room sync message (request or response with full state)
 */
export async function publishRoomSync(
  node: Libp2p,
  roomId: string,
  data: Uint8Array
): Promise<void> {
  const topic = TOPICS.room.sync(roomId)
  await publishToTopic(node, topic, data)
}

/**
 * Subscribe to global presence topic
 * The relay subscribes to this and forwards messages to all connected clients.
 * This enables peer discovery across all rooms.
 */
export function subscribeToGlobalPresence(
  node: Libp2p,
  handler: TopicHandler
): () => void {
  const topic = TOPICS.globalPresence
  return subscribeTopic(node, topic, handler)
}

/**
 * Publish presence announcement on global topic
 * This will be forwarded by the relay to all other connected clients.
 */
export async function publishGlobalPresence(
  node: Libp2p,
  data: Uint8Array
): Promise<void> {
  const topic = TOPICS.globalPresence
  await publishToTopic(node, topic, data)
}

// ============= ICS Hierarchy Topic Operations =============

/**
 * Subscribe to hierarchy escalations (for command room)
 */
export function subscribeToHierarchyEscalations(
  node: Libp2p,
  commandRoomId: string,
  handler: TopicHandler
): () => void {
  const topic = TOPICS.hierarchy.escalations(commandRoomId)
  return subscribeTopic(node, topic, handler)
}

/**
 * Publish an escalation to command room
 */
export async function publishEscalation(
  node: Libp2p,
  commandRoomId: string,
  data: Uint8Array
): Promise<void> {
  const topic = TOPICS.hierarchy.escalations(commandRoomId)
  await publishToTopic(node, topic, data)
}

/**
 * Subscribe to hierarchy broadcasts (for sector rooms)
 */
export function subscribeToHierarchyBroadcasts(
  node: Libp2p,
  commandRoomId: string,
  handler: TopicHandler
): () => void {
  const topic = TOPICS.hierarchy.broadcasts(commandRoomId)
  return subscribeTopic(node, topic, handler)
}

/**
 * Publish a broadcast from command room
 */
export async function publishBroadcast(
  node: Libp2p,
  commandRoomId: string,
  data: Uint8Array
): Promise<void> {
  const topic = TOPICS.hierarchy.broadcasts(commandRoomId)
  await publishToTopic(node, topic, data)
}

/**
 * Subscribe to resource assignments (for all hierarchy rooms)
 */
export function subscribeToHierarchyAssignments(
  node: Libp2p,
  commandRoomId: string,
  handler: TopicHandler
): () => void {
  const topic = TOPICS.hierarchy.assignments(commandRoomId)
  return subscribeTopic(node, topic, handler)
}

/**
 * Publish a resource assignment
 */
export async function publishAssignment(
  node: Libp2p,
  commandRoomId: string,
  data: Uint8Array
): Promise<void> {
  const topic = TOPICS.hierarchy.assignments(commandRoomId)
  await publishToTopic(node, topic, data)
}

/**
 * Subscribe to hierarchy structure changes
 */
export function subscribeToHierarchyStructure(
  node: Libp2p,
  commandRoomId: string,
  handler: TopicHandler
): () => void {
  const topic = TOPICS.hierarchy.structure(commandRoomId)
  return subscribeTopic(node, topic, handler)
}

/**
 * Publish a hierarchy structure change
 */
export async function publishHierarchyStructure(
  node: Libp2p,
  commandRoomId: string,
  data: Uint8Array
): Promise<void> {
  const topic = TOPICS.hierarchy.structure(commandRoomId)
  await publishToTopic(node, topic, data)
}

/**
 * Subscribe to sector status updates (for command room)
 */
export function subscribeToSectorStatus(
  node: Libp2p,
  commandRoomId: string,
  handler: TopicHandler
): () => void {
  const topic = TOPICS.hierarchy.sectorStatus(commandRoomId)
  return subscribeTopic(node, topic, handler)
}

/**
 * Publish sector status update (heartbeat)
 */
export async function publishSectorStatus(
  node: Libp2p,
  commandRoomId: string,
  data: Uint8Array
): Promise<void> {
  const topic = TOPICS.hierarchy.sectorStatus(commandRoomId)
  await publishToTopic(node, topic, data)
}

/**
 * Subscribe to all hierarchy topics for a command room
 * Useful for command room to receive all hierarchy-related messages
 */
export function subscribeToAllHierarchyTopics(
  node: Libp2p,
  commandRoomId: string,
  handlers: {
    onEscalation?: TopicHandler
    onBroadcast?: TopicHandler
    onAssignment?: TopicHandler
    onStructure?: TopicHandler
    onSectorStatus?: TopicHandler
    onOrganizerAssignment?: TopicHandler
    onRemoteCommand?: TopicHandler
    onBoundaryUpdate?: TopicHandler
  }
): () => void {
  const unsubscribes: (() => void)[] = []

  if (handlers.onEscalation) {
    unsubscribes.push(subscribeToHierarchyEscalations(node, commandRoomId, handlers.onEscalation))
  }
  if (handlers.onBroadcast) {
    unsubscribes.push(subscribeToHierarchyBroadcasts(node, commandRoomId, handlers.onBroadcast))
  }
  if (handlers.onAssignment) {
    unsubscribes.push(subscribeToHierarchyAssignments(node, commandRoomId, handlers.onAssignment))
  }
  if (handlers.onStructure) {
    unsubscribes.push(subscribeToHierarchyStructure(node, commandRoomId, handlers.onStructure))
  }
  if (handlers.onSectorStatus) {
    unsubscribes.push(subscribeToSectorStatus(node, commandRoomId, handlers.onSectorStatus))
  }
  if (handlers.onOrganizerAssignment) {
    unsubscribes.push(subscribeToOrganizerAssignments(node, commandRoomId, handlers.onOrganizerAssignment))
  }
  if (handlers.onRemoteCommand) {
    unsubscribes.push(subscribeToRemoteCommands(node, commandRoomId, handlers.onRemoteCommand))
  }
  if (handlers.onBoundaryUpdate) {
    unsubscribes.push(subscribeToBoundaryUpdates(node, commandRoomId, handlers.onBoundaryUpdate))
  }

  return () => {
    for (const unsubscribe of unsubscribes) {
      unsubscribe()
    }
  }
}

/**
 * Subscribe to hierarchy topics for a sector room
 * Sectors need broadcasts, assignments, and structure changes
 */
export function subscribeToHierarchyTopicsForSector(
  node: Libp2p,
  commandRoomId: string,
  handlers: {
    onBroadcast?: TopicHandler
    onAssignment?: TopicHandler
    onStructure?: TopicHandler
    onOrganizerAssignment?: TopicHandler
    onRemoteCommand?: TopicHandler
    onBoundaryUpdate?: TopicHandler
  }
): () => void {
  const unsubscribes: (() => void)[] = []

  if (handlers.onBroadcast) {
    unsubscribes.push(subscribeToHierarchyBroadcasts(node, commandRoomId, handlers.onBroadcast))
  }
  if (handlers.onAssignment) {
    unsubscribes.push(subscribeToHierarchyAssignments(node, commandRoomId, handlers.onAssignment))
  }
  if (handlers.onStructure) {
    unsubscribes.push(subscribeToHierarchyStructure(node, commandRoomId, handlers.onStructure))
  }
  if (handlers.onOrganizerAssignment) {
    unsubscribes.push(subscribeToOrganizerAssignments(node, commandRoomId, handlers.onOrganizerAssignment))
  }
  if (handlers.onRemoteCommand) {
    unsubscribes.push(subscribeToRemoteCommands(node, commandRoomId, handlers.onRemoteCommand))
  }
  if (handlers.onBoundaryUpdate) {
    unsubscribes.push(subscribeToBoundaryUpdates(node, commandRoomId, handlers.onBoundaryUpdate))
  }

  return () => {
    for (const unsubscribe of unsubscribes) {
      unsubscribe()
    }
  }
}

// ============= Organizer Assignment Topic Operations =============

/**
 * Subscribe to organizer assignment updates
 */
export function subscribeToOrganizerAssignments(
  node: Libp2p,
  commandRoomId: string,
  handler: TopicHandler
): () => void {
  const topic = TOPICS.hierarchy.organizerAssignments(commandRoomId)
  return subscribeTopic(node, topic, handler)
}

/**
 * Publish an organizer assignment
 */
export async function publishOrganizerAssignment(
  node: Libp2p,
  commandRoomId: string,
  data: Uint8Array
): Promise<void> {
  const topic = TOPICS.hierarchy.organizerAssignments(commandRoomId)
  await publishToTopic(node, topic, data)
}

// ============= Remote Commands Topic Operations =============

/**
 * Subscribe to remote commands (for sector rooms)
 */
export function subscribeToRemoteCommands(
  node: Libp2p,
  commandRoomId: string,
  handler: TopicHandler
): () => void {
  const topic = TOPICS.hierarchy.commands(commandRoomId)
  return subscribeTopic(node, topic, handler)
}

/**
 * Publish a remote command from command room
 */
export async function publishRemoteCommand(
  node: Libp2p,
  commandRoomId: string,
  data: Uint8Array
): Promise<void> {
  const topic = TOPICS.hierarchy.commands(commandRoomId)
  await publishToTopic(node, topic, data)
}

// ============= Boundary Updates Topic Operations =============

/**
 * Subscribe to boundary updates (for sector rooms)
 */
export function subscribeToBoundaryUpdates(
  node: Libp2p,
  commandRoomId: string,
  handler: TopicHandler
): () => void {
  const topic = TOPICS.hierarchy.boundaries(commandRoomId)
  return subscribeTopic(node, topic, handler)
}

/**
 * Publish a boundary update
 */
export async function publishBoundaryUpdate(
  node: Libp2p,
  commandRoomId: string,
  data: Uint8Array
): Promise<void> {
  const topic = TOPICS.hierarchy.boundaries(commandRoomId)
  await publishToTopic(node, topic, data)
}
